'use strict';

import dayjs from 'dayjs';
import { getTierSampleBounds, resolveStudySizeId } from './studyBuilder.config.js';

const DEFAULT_WEEKS_TO_COMPLETION = 7;

export const REFER_TO_SALES_COST_DISPLAY = 'Contact sales for pricing';

export const ESTIMATE_ERROR_REF_SAVE = 'EST-SAVE';
export const ESTIMATE_ERROR_REF_LOAD = 'EST-LOAD';
export const ESTIMATE_ERROR_REF_LIMIT = 'EST-LIMIT';

/** Shown when POST /estimate returns 429 (monthly estimate limit reached). */
export const ESTIMATE_MONTHLY_LIMIT_MESSAGE =
	"You've already requested two estimates this month. Please contact us if you need help with additional estimates.";

/** Shown when POST /estimate returns 500 (persistence failed after calculate). */
export const ESTIMATE_SAVE_FAILED_MESSAGE =
	`We couldn't save your estimate request. Please try again or contact us for help. [${ESTIMATE_ERROR_REF_SAVE}]`;

/** Shown when the estimate could not be calculated or returned (network, non-500 API errors, etc.). */
export const ESTIMATE_LOAD_FAILED_MESSAGE =
	`We couldn't calculate or display your estimate. Please try again. [${ESTIMATE_ERROR_REF_LOAD}]`;

/**
 * Indicative completion date until the API provides one.
 * @param {string} [plannedShippingDate]
 * @returns {import('dayjs').Dayjs}
 */
export function estimateIndicativeCompletionDate(plannedShippingDate) {
	const start = plannedShippingDate ? dayjs(plannedShippingDate) : dayjs();
	return start.add(DEFAULT_WEEKS_TO_COMPLETION, 'week');
}

/**
 * @param {{
 *   fitSelections: Record<string, unknown>,
 *   totalSamples: number,
 *   quoteDraft?: { lineItems?: unknown[], sampleInfo?: Record<string, unknown> } | null,
 * }} params
 * @returns {Record<string, unknown>}
 */
export function buildStudyBuilderEstimateRequest({ fitSelections, totalSamples, quoteDraft }) {
	const body = {
		sampleCount: Math.max(1, Math.round(Number(totalSamples)) || 1),
		selections: fitSelections,
	};

	if (fitSelections.statsServices === 'yes' || fitSelections.statsServices === 'no') {
		body.statsServices = fitSelections.statsServices;
	}

	if (fitSelections.statsServices === 'yes' && fitSelections.interpretationLevel) {
		body.interpretationLevel = fitSelections.interpretationLevel;
	}

	if (quoteDraft && typeof quoteDraft === 'object') {
		body.quoteDraft = quoteDraft;
	}

	return body;
}

/** Build estimate POST body from GET /study-builder/draft response. */
export function buildStudyBuilderEstimateRequestFromDraft(draft) {
	return buildStudyBuilderEstimateRequest({
		fitSelections: draft.selections,
		totalSamples: draft.sampleCount,
		quoteDraft: draft.quoteDraft,
	});
}

/**
 * User-facing message for estimate POST failures (RTK Query unwrap errors).
 * Save failures (HTTP 500) and load/calculate failures use distinct text with reference codes
 * so support can correlate user reports to failure type.
 * @param {unknown} error
 * @returns {string}
 */
export function resolveStudyBuilderEstimateFetchErrorMessage(error) {
	const status = Number(error?.status ?? error?.originalStatus);
	if (status === 500) {
		return ESTIMATE_SAVE_FAILED_MESSAGE;
	}

	if (status === 429) {
		const apiMessage = typeof error?.data?.error === 'string' ? error.data.error.trim() : '';
		const message = apiMessage || ESTIMATE_MONTHLY_LIMIT_MESSAGE;
		return `${message} [${ESTIMATE_ERROR_REF_LIMIT}]`;
	}

	const apiMessage = typeof error?.data?.error === 'string' ? error.data.error.trim() : '';
	if (apiMessage) {
		return `${apiMessage} [${ESTIMATE_ERROR_REF_LOAD}]`;
	}

	return ESTIMATE_LOAD_FAILED_MESSAGE;
}

/**
 * @param {Record<string, unknown>} fitSelections
 * @param {number} totalSamples
 * @returns {string|null}
 */
export function buildQuoteSampleBasisDisplay(fitSelections, totalSamples) {
	const studySizeId = resolveStudySizeId(fitSelections.studySize, totalSamples);
	const useTierRange =
		Boolean(fitSelections.studySize)
		&& ((fitSelections.sampleCountMode || 'tier') === 'tier'
			|| (fitSelections.matrixScopeMode || 'single') !== 'single');

	if (useTierRange) {
		const { lo, hi } = getTierSampleBounds(studySizeId);
		return `${lo}–${hi} (range)`;
	}

	const n = Math.round(Number(fitSelections.exactSampleCount)) || totalSamples;
	return `${n} (exact)`;
}

/**
 * @param {Record<string, unknown>|null|undefined} response
 * @returns {{ kind: 'referToSales'|'openEnded'|'range', estimatedCostDisplay: string|null, referToSales: boolean, status?: string, sampleCountTier?: string }}
 */
export function mapStudyBuilderEstimatePresentation(response) {
	const status = String(response?.status || '');
	const referToSales = Boolean(response?.referToSales) || status === 'ReferToSales';

	if (referToSales || status === 'UnsupportedInputs' || status === 'RangeNotFound') {
		return {
			kind: 'referToSales',
			estimatedCostDisplay: null,
			referToSales: true,
			status,
		};
	}

	if (response?.maxEstimateIsOpenEnded) {
		return {
			kind: 'openEnded',
			estimatedCostDisplay: response.estimatedCostDisplay ? String(response.estimatedCostDisplay) : null,
			referToSales: false,
			status,
			sampleCountTier: response.sampleCountTier ? String(response.sampleCountTier) : undefined,
		};
	}

	return {
		kind: 'range',
		estimatedCostDisplay: response?.estimatedCostDisplay ? String(response.estimatedCostDisplay) : null,
		referToSales: false,
		status,
		sampleCountTier: response?.sampleCountTier ? String(response.sampleCountTier) : undefined,
	};
}

/**
 * @param {{ kind: string, estimatedCostDisplay?: string|null, referToSales?: boolean }} presentation
 * @returns {string|null}
 */
export function resolveEstimatedCostDisplay(presentation) {
	if (!presentation) return null;
	if (presentation.referToSales || presentation.kind === 'referToSales') return REFER_TO_SALES_COST_DISPLAY;
	return presentation.estimatedCostDisplay ?? null;
}
